'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { KeyboardArbitrationContext } from '#pplx/frontend/composer-sdk/internal/keyboard/keyboardArbitration.js';
import { isKeyboardCommandEligible } from '#pplx/frontend/composer-sdk/internal/keyboard/keyboardCapabilityUtils.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { COMMAND_PRIORITY_HIGH, KEY_ARROW_DOWN_COMMAND, KEY_ARROW_UP_COMMAND } from 'lexical';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
function toKeyboardArbitrationEvent(event) {
    return {
        altKey: event.altKey,
        code: event.code,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        repeat: event.repeat,
        shiftKey: event.shiftKey,
    };
}
/**
 * Runs arrow-key handlers in priority order until one handles or reserves the key.
 *
 * @returns Whether Lexical should stop sending the key to lower-priority handlers.
 */
function runCandidates(event, key, candidates) {
    if (!isKeyboardCommandEligible(event))
        return false;
    const keyboardEvent = toKeyboardArbitrationEvent(event);
    // Give higher-priority candidates first refusal while preserving registration order for ties.
    // `null` still consumes the Lexical command so its menu handler cannot run afterward, but it
    // deliberately leaves the browser default intact for contenteditable caret movement.
    for (const candidate of [...candidates.values()].sort((a, b) => b.priority - a.priority)) {
        const result = candidate.onKeyDown(keyboardEvent, key);
        if (result === false)
            continue;
        // A claimed suggestion move must not also move the contenteditable caret. `null` is the
        // explicit reservation for handlers that need the browser's native caret movement instead.
        if (result === true)
            event.preventDefault();
        return true;
    }
    return false;
}
/** Owns the Lexical ArrowUp/ArrowDown command entrypoints used by Composer extensions. */
export function KeyboardArbitrationHost({ children }) {
    const [editor] = useLexicalComposerContext();
    const candidatesRef = useRef(new Map());
    const register = useCallback((candidate) => {
        if (!Number.isInteger(candidate.priority) || candidate.priority < 0) {
            throw new Error('Keyboard candidate priority must be a non-negative integer');
        }
        const id = Symbol('keyboard-candidate');
        candidatesRef.current.set(id, candidate);
        return () => candidatesRef.current.delete(id);
    }, []);
    const registry = useMemo(() => ({ register }), [register]);
    useEffect(() => {
        const registerCommand = (command, key) => editor.registerCommand(command, (event) => {
            if (editor.isComposing())
                return false;
            return runCandidates(event, key, candidatesRef.current);
        }, 
        // One high-priority entrypoint arbitrates SDK typeahead before plugin
        // candidates; returning false leaves the command to Lexical/native caret movement.
        COMMAND_PRIORITY_HIGH);
        return mergeRegister(registerCommand(KEY_ARROW_UP_COMMAND, 'ArrowUp'), registerCommand(KEY_ARROW_DOWN_COMMAND, 'ArrowDown'));
    }, [editor]);
    return (_jsx(KeyboardArbitrationContext.Provider, { value: registry, children: children }));
}
//# sourceMappingURL=KeyboardArbitrationHost.js.map